import {useEffect, useState} from "react";
import {surgeryRequestsService} from "../services/surgeryRequests-service";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";
import {SurgeryRequestItem} from "../types/surgeryRequests";
import {useForm} from "antd/es/form/Form";
import {Query} from "appwrite";
import {List, Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import {validateSurgeryRequestConflicts} from "../helpers/validateSurgeryRequestConflicts";
import dayjs from "dayjs";
import {SURGERY_REQUEST_CONFLICT_MESSAGES} from "../constants/messages";
import useOperationStaffController from "./operationStaff-controller";
import {getOperationStaffNames} from "../helpers/getOperationStaffNames";
import {canEditSurgeryRequest} from "../helpers/canEditSurgeryRequest";
import {useAuth} from "../context/authContext";

const EMPTY_QUERIES: string[] = []

type UseSurgeryRequestsControllerProps = {
  queries?: string[]
  enabled?: boolean
}

export const useSurgeryRequestsController = (props?: UseSurgeryRequestsControllerProps) => {
  const {currentUser} = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [list, setList] = useState<SurgeryRequestItem[]>([])
  const [selectedItem, setSelectedItem] = useState<SurgeryRequestItem | undefined>(undefined)
  const [lastUpdate, setLastUpdate] = useState(0)
  const [form] = useForm()
  const {operationStaffList} = useOperationStaffController()

  const queries = props?.queries ?? EMPTY_QUERIES
  const enabled = props?.enabled ?? true


  useEffect(() => {
    let active = true
    setList([])
    if (!enabled) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    surgeryRequestsService.list([Query.orderDesc('$createdAt'), ...queries])
      .then(list => {
        if (active) setList(list)
      })
      .catch(error => {
        if (!active) return
        console.error(error)
        showErrorNotification({message: "Помилка при отриманні заявок на операції!"})
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => { active = false }
  }, [lastUpdate, queries, enabled])

  const changeLastUpdate = () => {
    setLastUpdate(Date.now())
  }

  const toggleModal = () => {
    const newOpenValue = !isModalOpen
    setIsModalOpen(newOpenValue)
    if (!newOpenValue) {
      setSelectedItem(undefined)
    }
  }

  const toggleDetailsModal = () => {
    const newOpenValue = !isDetailsModalOpen
    setIsDetailsModalOpen(newOpenValue)
    if (!newOpenValue) {
      setSelectedItem(undefined)
    }
  }

  const createSurgeryRequestHandler = async (data: SurgeryRequestItem) => {
    setIsLoading(true)
    try {
      const queries = [
        Query.equal('operationTime', dayjs(data.operationTime).toISOString()),
      ]

      const operationsAtSameTime = await surgeryRequestsService.list(queries)

      const conflictData = validateSurgeryRequestConflicts({
        item: data,
        list: operationsAtSameTime,
      })

      if (conflictData) {
        if (conflictData.roomConflict) {
          showErrorNotification({
            message: SURGERY_REQUEST_CONFLICT_MESSAGES.ROOM,
          })
        }

        if (conflictData.conflictedStaffIds.length > 0) {
          const conflictedStaffNames = getOperationStaffNames({
            staffList: operationStaffList,
            ids: conflictData.conflictedStaffIds
          })

          showErrorNotification({
            message: SURGERY_REQUEST_CONFLICT_MESSAGES.STAFF,
            description: <List
              size="small"
              dataSource={conflictedStaffNames}
              renderItem={(name) => <List.Item>{name}</List.Item>}
            />
          })
        }
        return
      }

      await surgeryRequestsService.createSurgeryRequests(data)
      toggleModal()
      changeLastUpdate()
      openSuccessNotification('Операція успішно додана!')
    } catch (error) {
      showErrorNotification({
        message: 'Помилка додавання операції!',
        description: JSON.stringify(error),
      })
    } finally {
      setIsLoading(false)
    }
  }

  const updateSurgeryRequestHandler = async (data: SurgeryRequestItem) => {
    if (!selectedItem || selectedItem.$id !== data.$id || !canEditSurgeryRequest(selectedItem, currentUser?.id)) {
      showErrorNotification({message: "Редагувати можна лише власні заявки, час операції яких ще не настав."})
      return
    }
    if (!dayjs(data.operationTime).isAfter(dayjs())) {
      showErrorNotification({message: "Дата та час операції мають бути в майбутньому."})
      return
    }
    setIsLoading(true)
    try {
      const createdSurgeryRequest = await surgeryRequestsService.updateSurgeryRequest(data)
      toggleModal()
      changeLastUpdate()
      openSuccessNotification("Операцію успішно оновлено!")
    } catch (error) {
      showErrorNotification({message: "Помилка оновлення операції!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }


  const detailsButtonHandler = (item: SurgeryRequestItem) => {
    setSelectedItem(item)
    toggleDetailsModal()
  }

  const editButtonHandler = (item: SurgeryRequestItem) => {
    if (!canEditSurgeryRequest(item, currentUser?.id)) {
      showErrorNotification({message: "Редагувати можна лише власні заявки, час операції яких ще не настав."})
      return
    }
    setSelectedItem(item)
    toggleModal()
  }

  const deleteSurgeryRequest = async (id: string) => {
    setIsLoading(true)
    try {
      await surgeryRequestsService.delete(id)
      openSuccessNotification("Операцію було успішно видалено!")
      changeLastUpdate()
    } catch (error) {
      showErrorNotification({message: "Помилка видалення операції!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }


  const confirmDeleteSurgeryRequest = (id: string) => {
    const {confirm} = Modal;

    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити операцію?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          await deleteSurgeryRequest(id)
        },
      });

  }


  return {
    list,
    isModalOpen,
    isLoading,
    toggleModal,
    selectedItem,
    form,
    createSurgeryRequestHandler,
    updateSurgeryRequestHandler,
    isDetailsModalOpen,
    toggleDetailsModal,
    detailsButtonHandler,
    editButtonHandler,
    confirmDeleteSurgeryRequest
  }
}
